import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { loadTurnstile } from "../../lib/loadTurnstile";
import { useLang } from "../../contexts/LanguageContext";

/**
 * Cloudflare Turnstile widget for any React form.
 *
 * Fetches the site key from `/api/settings.turnstile_site_key` once and
 * renders the widget. When the site key is empty the component renders
 * nothing and the parent form submits normally — graceful degradation.
 *
 * Props:
 *   onToken(token)  — called with the verified token on success, with "" on
 *                     error/expiry so the parent can disable the submit btn.
 *   action          — Cloudflare "action" bound to the token ("login", "newsletter",
 *                     "contact"). Backend validates this to prevent token reuse
 *                     across different forms.
 *   className       — extra classes for the wrapper.
 */
export default function TurnstileField({ onToken, action = "generic", className = "" }) {
  const { lang } = useLang();
  const ref = useRef(null);
  const widgetId = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setSiteKey((r.data?.turnstile_site_key || "").trim()))
      .catch(() => setSiteKey(""));
  }, []);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let mounted = true;
    let localWidgetId = null;

    loadTurnstile().then(() => {
      // `loadTurnstile()` resolves only once `window.turnstile` is fully
      // initialised — no `turnstile.ready()` wrapper needed. Skipping it
      // also avoids a race with React StrictMode's double-mount where the
      // ready callback would fire after the first cleanup destroyed the
      // container ref.
      if (!mounted || !window.turnstile || !ref.current) return;
      try {
        localWidgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action,
          theme: "auto",
          language: lang === "es" ? "es-ES" : lang === "fr" ? "fr" : lang === "ar" ? "ar-EG" : "en",
          callback: (token) => onToken?.(token),
          "error-callback": () => onToken?.(""),
          "expired-callback": () => {
            onToken?.("");
            if (localWidgetId && window.turnstile) window.turnstile.reset(localWidgetId);
          },
          "timeout-callback": () => {
            onToken?.("");
            if (localWidgetId && window.turnstile) window.turnstile.reset(localWidgetId);
          },
        });
        widgetId.current = localWidgetId;
      } catch { /* render failed — leave the form usable without CAPTCHA */ }
    }).catch(() => {});

    return () => {
      mounted = false;
      if (localWidgetId && window.turnstile) {
        try { window.turnstile.remove(localWidgetId); } catch { /* widget already gone */ }
      }
    };
    // Widget is language-aware: re-mount on locale change so the challenge
    // renders in the visitor's current language.
  }, [siteKey, action, lang, onToken]);

  // Not configured yet — render nothing, keeping the form functional.
  if (siteKey === "") return null;
  return <div ref={ref} data-testid={`turnstile-${action}`} className={className} />;
}
